import { useState, useEffect, useRef } from "react";
import { db } from "./firebase";
import { collection, addDoc, onSnapshot, updateDoc, deleteDoc, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { auth } from "./firebase";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  EmailAuthProvider,
  signInWithCredential,
  reauthenticateWithCredential,
  deleteUser
} from "firebase/auth";
import logo from "./assets/logo.png";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";
import ReCAPTCHA from "react-google-recaptcha";
import { storage } from "./firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { Keyboard } from '@capacitor/keyboard';

const PlacesPlugin = registerPlugin("PlacesPlugin");

const libraries = ["places"];

// Downtown Houston coordinates
const SERVICE_CENTER = { lat: 29.7604, lng: -95.3698 };
const SERVICE_RADIUS_MILES = 50;

function getDistanceInMiles(lat1, lng1, lat2, lng2) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 3958.8; // Earth radius in miles

  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

function isWithinServiceArea(lat, lng) {
  const distance = getDistanceInMiles(
    SERVICE_CENTER.lat,
    SERVICE_CENTER.lng,
    lat,
    lng
  );
  return distance <= SERVICE_RADIUS_MILES;
}

function SuccessPage() {
  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      background: "#f3f4f6",
      padding: "20px"
    }}>
      <div style={{
        background: "white",
        padding: "40px",
        borderRadius: "20px",
        textAlign: "center",
        maxWidth: "500px",
        width: "100%",
        boxShadow: "0 20px 40px rgba(0,0,0,0.08)"
      }}>
        <h1 style={{ color: "#16a34a" }}>
          Payment Received
        </h1>

        <p
          style={{
            marginTop: "15px",
            color: "#6b7280",
            lineHeight: "1.6"
          }}
        >
          Thank you! Your payment has been received successfully.
        </p>

        <p
          style={{
            color: "#6b7280",
            marginBottom: "25px"
          }}
        >
          Your pickup is now confirmed and we're assigning one of our trusted pickup partners.
        </p>

        <p
          style={{
            color: "#6b7280",
            marginBottom: "25px"
          }}
        >
          We'll email you again when your pickup partner is on the way.
        </p>

        <a
          href="/"
          style={{
            display: "inline-block",
            background: "#2563eb",
            color: "white",
            padding: "12px 24px",
            borderRadius: "10px",
            textDecoration: "none",
            fontWeight: "600"
          }}
        >
          Back Home
        </a>
      </div>
    </div>
  );
}

function QuoteSuccessPage({ onNewRequest }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "#f3f4f6",
        padding: "20px"
      }}
    >
      <div
        style={{
          background: "white",
          padding: "40px",
          borderRadius: "20px",
          textAlign: "center",
          maxWidth: "500px",
          width: "100%",
          boxShadow: "0 20px 40px rgba(0,0,0,0.08)"
        }}
      >
        <h1 style={{ color: "#2563eb" }}>
          Request Received
        </h1>

        <p style={{ color: "#6b7280", marginTop: "15px" }}>
          Thank you! We've received your pickup request and photos.
        </p>

        <p style={{ color: "#6b7280", marginBottom: "15px" }}>
          Our team is reviewing your request and will email you a personalized quote as soon as possible.
        </p>

        <p style={{ color: "#6b7280", marginBottom: "25px" }}>
          No payment is required until you approve your quote.
        </p>

        <button
          onClick={onNewRequest}
          style={{
            background: "#2563eb",
            color: "white",
            border: "none",
            borderRadius: "10px",
            padding: "12px 24px",
            cursor: "pointer"
          }}
        >
          New Request
        </button>
      </div>
    </div>
  );
}


function App() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [view, setView] = useState("customer");
  const [statusFilter, setStatusFilter] = useState("all");
  const [vendorName, setVendorName] = useState("");
  const [vendorPhone, setVendorPhone] = useState("");
  const [vendors, setVendors] = useState([]);
  const [vendorEmail, setVendorEmail] = useState("");
  const [address, setAddress] = useState("");
  const [requests, setRequests] = useState([]);
  const [editingVendorId, setEditingVendorId] = useState(null);
  const [editVendorName, setEditVendorName] = useState("");
  const [editVendorPhone, setEditVendorPhone] = useState("");
  const [editVendorEmail, setEditVendorEmail] = useState("");
  const [vendorView, setVendorView] = useState(false);
  const [vendorEmailLogin, setVendorEmailLogin] = useState("");
  const [loggedInVendor, setLoggedInVendor] = useState(null);
  const [vendorPassword, setVendorPassword] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [prevRequests, setPrevRequests] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const autocompleteRef = useRef(null);
  const [showHistory, setShowHistory] = useState(false);
  const [user, setUser] = useState(null);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [captchaValue, setCaptchaValue] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [pickupType, setPickupType] = useState("");
  const [itemType, setItemType] = useState("");
  const { isLoaded } = useJsApiLoader({
  googleMapsApiKey: Capacitor.isNativePlatform()
    ? ""
    : "AIzaSyCUQNh53y7HmgHjtDDiYeQpFgJVFOFF_RQ", libraries});
  const [quoteSubmitted, setQuoteSubmitted] = useState(false);
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [addressPredictions, setAddressPredictions] = useState([]);
  const [addressSearching, setAddressSearching] = useState(false);
  const [addressError, setAddressError] = useState("");

  const isPaymentSuccess =
  new URLSearchParams(window.location.search)
    .get("success") === "true";

  useEffect(() => {

    // Nobody authenticated yet
    if (!user) {
      setRequests([]);
      setPrevRequests([]);
      return;
    }

    let requestsQuery;

    // ADMIN: may load all pickup requests
    if (isAdmin) {

      requestsQuery = collection(
        db,
        "pickup_requests"
      );

    }

    // VENDOR: load ONLY jobs assigned to this vendor
    else if (loggedInVendor?.id) {

      requestsQuery = query(
        collection(db, "pickup_requests"),
        where(
          "vendorId",
          "==",
          loggedInVendor.id
        )
      );

    }

    // Signed in but role hasn't finished loading yet
    else {
      // CUSTOMER: only their own pickup requests
      requestsQuery = query(
        collection(db, "pickup_requests"),
        where("customerUid", "==", user.uid)
      );
    }

    const unsubscribeRequests = onSnapshot(
      requestsQuery,

      (snapshot) => {

        const data = snapshot.docs.map(
          (document) => ({
            id: document.id,
            ...document.data()
          })
        );

        setPrevRequests(data);
        setRequests(data);
      },

      (error) => {
        console.error(
          "Pickup request listener error:",
          error
        );
      }
    );

    return () => unsubscribeRequests();

  }, [
    user,
    isAdmin,
    loggedInVendor?.id
  ]);

  useEffect(() => {

    // ADMIN: load all vendors
    if (isAdmin) {

      const unsubscribeVendors = onSnapshot(
        collection(db, "vendors"),
        (snapshot) => {

          const data = snapshot.docs.map((document) => ({
            id: document.id,
            ...document.data()
          }));

          setVendors(data);
        },

        (error) => {
          console.error(
            "Vendor listener error:",
            error
          );
        }
      );

      return () => unsubscribeVendors();
    }

    // VENDOR: only keep their own vendor record
    if (loggedInVendor?.id) {
      setVendors([loggedInVendor]);
      return;
    }

    // CUSTOMER / LOGGED OUT
    setVendors([]);

  }, [
    isAdmin,
    loggedInVendor?.id
  ]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const showListener = Keyboard.addListener('keyboardWillShow', () => {
      // Optional: add custom scroll/adjustment logic here if needed later
    });

    const hideListener = Keyboard.addListener('keyboardWillHide', () => {
      // Optional: cleanup logic here if needed later
    });

    return () => {
      showListener.remove();
      hideListener.remove();
    };
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      console.log("AUTH STATE CHANGED:", currentUser);
      setUser(currentUser);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const restoreUserRole = async () => {

      if (!user) {
        setIsAdmin(false);
        setLoggedInVendor(null);
        return;
      }

      const signedInEmail = user.email?.toLowerCase();

      // ADMIN
      if (signedInEmail === "admin@onewast.com") {
        setIsAdmin(true);
        setLoggedInVendor(null);
        return;
      }

      // NOT ADMIN
      setIsAdmin(false);

      try {

        // Look up this Firebase user's vendor mapping
        const mappingRef = doc(
          db,
          "vendor_users",
          user.uid
        );

        const mappingSnap = await getDoc(mappingRef);

        if (!mappingSnap.exists()) {
          console.log("No vendor mapping found.");
          setLoggedInVendor(null);
          return;
        }

        const vendorId = mappingSnap.data().vendorId;

        // Load the actual vendor record
        const vendorRef = doc(
          db,
          "vendors",
          vendorId
        );

        const vendorSnap = await getDoc(vendorRef);

        if (!vendorSnap.exists()) {
          console.log("Vendor record not found.");
          setLoggedInVendor(null);
          return;
        }

        setLoggedInVendor({
          id: vendorSnap.id,
          ...vendorSnap.data()
        });

      } catch (error) {
        console.error(
          "Error restoring vendor:",
          error
        );

        setLoggedInVendor(null);
      }
    };

    restoreUserRole();

  }, [user]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (!photoViewerOpen) return;

    const handleKeyDown = (event) => {
      switch (event.key) {
        case "ArrowLeft":
          setCurrentPhotoIndex((prev) =>
            Math.max(prev - 1, 0)
          );
          break;

        case "ArrowRight":
          setCurrentPhotoIndex((prev) =>
            Math.min(prev + 1, selectedPhotos.length - 1)
          );
          break;

        case "Escape":
          setPhotoViewerOpen(false);
          break;

        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () =>
      window.removeEventListener("keydown", handleKeyDown);

  }, [photoViewerOpen, selectedPhotos]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    // Auto-close the Stripe browser tab once payment succeeds
    const pageLoadedListener = Browser.addListener(
      "browserPageLoaded",
      async () => {
        // We can't read the URL directly, but Stripe's success_url
        // is onewast.com/?success=true — closing here is safe since
        // this only fires on page navigations inside the in-app browser
      }
    );

    // Reset the form whenever the in-app browser is closed,
    // whether payment succeeded or the user backed out
    const finishedListener = Browser.addListener(
      "browserFinished",
      () => {
        setPickupType("");
        setItemType("");
        setAddress("");
        setPhotos([]);
        setCaptchaValue(null);
        setAddressError("");
        setAddressPredictions([]);
        setLoading(false);
      }
    );

    return () => {
      pageLoadedListener.remove();
      finishedListener.remove();
    };
  }, []);

  useEffect(() => {
    if (user && !isAdmin && !loggedInVendor) {
      setCustomerEmail(user.email || "");
    }

    if (!user) {
      setCustomerEmail("");
    }
  }, [user, isAdmin, loggedInVendor]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const activeUser = user || auth.currentUser;

    console.log("SUBMIT GUARD CHECK:", {
      user,
      authCurrentUser: auth.currentUser,
      activeUser,
      isAdmin,
      loggedInVendor
    });

    if (!activeUser || isAdmin || loggedInVendor) {
      alert(
        "Please sign in with a customer account before requesting a pickup."
      );
      return;
    }

    // Prevent rapid double-click submits
    if (loading) return;

    // Validate required fields
    if (!pickupType || !itemType || !address || !customerEmail) {
      alert("Please complete all fields.");
      return;
    }

    if (addressError) {
      alert("Please select a valid address within our service area before submitting.");
      return;
    }

    // Validate reCAPTCHA
    if (!captchaValue) {
      alert("Please complete the reCAPTCHA.");
      return;
    }

    if (photos.length === 0) {
      alert("Please upload at least one photo.");
      return;
    }

    setLoading(true);

    try {

      if (pickupType === "custom") {

        const photoUrls = [];

        for (const photo of photos) {

          const storageRef = ref(
            storage,
            `quote-photos/${Date.now()}-${photo.name}`
          );

          await uploadBytes(storageRef, photo);

          const url = await getDownloadURL(storageRef);

          photoUrls.push(url);
        }

        await addDoc(collection(db, "pickup_requests"), {
          pickupType,
          itemType,
          address,
          customerEmail,
          customerUid: activeUser.uid,
          photoUrls,
          status: "quote_requested",
          createdAt: Date.now()
        });

        setQuoteSubmitted(true);

        setPickupType("");
        setItemType("");
        setAddress("");
        setPhotos([]);
        setCaptchaValue(null);

        localStorage.removeItem("lastCheckoutTime");

        setLoading(false);

        return;

      }

      // Prevent duplicate checkout creation within 2 minutes
      const lastCheckoutTime = localStorage.getItem("lastCheckoutTime");
      const now = Date.now();

      if (
        lastCheckoutTime &&
        now - Number(lastCheckoutTime) < 120000
      ) {
        alert("Checkout already started recently. Please wait a moment.");
        setLoading(false);
        return;
      }

      localStorage.setItem("lastCheckoutTime", now.toString());

      // Save request temporarily
      localStorage.setItem(
        "pendingRequest",
        JSON.stringify({
          pickupType,
          itemType,
          address,
          customerEmail,
        })
      );

      // Call Firebase Function
      const response = await fetch(
        "https://us-central1-onewast.cloudfunctions.net/createCheckoutSession",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            pickupType,
            itemType,
            address,
            customerEmail,
            customerUid: activeUser.uid,
          }),
        }
      );

      const text = await response.text();

      console.log("RAW RESPONSE:", text);

      const data = JSON.parse(text);

      if (!data.url) {
        throw new Error("No URL returned from backend");
      }

      if (Capacitor.isNativePlatform()) {
        await Browser.open({ url: data.url });
        setLoading(false);
      } else {
        window.location.href = data.url;
      }

    } catch (error) {
      console.error(error);

      alert(
        "We couldn't start checkout. Please check your connection and try again, or contact support@onewast.com if this keeps happening."
      );

      localStorage.removeItem("lastCheckoutTime");

      setLoading(false);
    }
  };

  const sendVendorAssignment = async (
    vendorEmail,
    itemType,
    address
  ) => {

    try {

      const response = await fetch(
        "https://us-central1-onewast.cloudfunctions.net/sendVendorAssignment",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            vendorEmail,
            itemType,
            address,
          }),
        }
      );

      const data = await response.json();

      if (!data.success) {
        alert("Vendor email failed.");
      }

    } catch (err) {
      console.error(err);
    }

  };

  const sendPickupCompleted = async (
    customerEmail,
    itemType,
    address
  ) => {

    try {

      const response = await fetch(
        "https://us-central1-onewast.cloudfunctions.net/sendPickupCompleted",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            customerEmail,
            itemType,
            address,
          }),
        }
      );

      const data = await response.json();

      if (!data.success) {
        alert("Completion email failed.");
      }

    } catch (err) {
      console.error(err);
    }

  };

  const sendVendorOnTheWay = async (
    customerEmail,
    itemType,
    address
  ) => {

    try {

      const response = await fetch(
        "https://us-central1-onewast.cloudfunctions.net/sendVendorOnTheWay",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            customerEmail,
            itemType,
            address,
          }),
        }
      );

      const data = await response.json();

      if (!data.success) {
        alert("On the way email failed.");
      }

    } catch (err) {
      console.error(err);
    }

  };

  const updateRequest = async (id, newStatus, vendorId, request) => {
    
    console.log("UPDATE REQUEST CALLED");
    console.log("Status:", newStatus);
    console.log("Vendor:", vendorId);
    const ref = doc(db, "pickup_requests", id);

    let updateData = {
      status: newStatus,
      vendorId: vendorId,
      vendor: vendors.find(v => v.id === vendorId)?.name || ""
    };

    if (newStatus === "assigned") {
      updateData.assignedAt = new Date();
    }

    if (newStatus === "accepted") {
      updateData.acceptedAt = new Date();
    }

    if (newStatus === "pending") {
      updateData.previousVendor = request.vendor || "";
      updateData.vendorId = "";
      updateData.vendor = "";
      updateData.declinedAt = new Date();
    }

    if (newStatus === "completed") {
      updateData.completedAt = new Date();
    }

    if (newStatus === "completed" && request.customerEmail) {
      sendPickupCompleted(
        request.customerEmail,
        request.itemType,
        request.address
      );
    }

    await updateDoc(ref, updateData);

    if (newStatus === "on_the_way" && request.customerEmail) {
      await sendVendorOnTheWay(
        request.customerEmail,
        request.itemType,
        request.address
      );
    }

    if (newStatus === "assigned" && vendorId) {
      
      console.log("ASSIGNMENT BLOCK RUNNING");
      console.log("Vendor ID:", vendorId);

      const selectedVendor = vendors.find(v => v.id === vendorId);

      console.log("Selected Vendor:", selectedVendor);
      
      if (selectedVendor?.email) {
        sendVendorAssignment(
          selectedVendor.email,
          request.itemType,
          request.address
        );
      }

      alert(`Job sent to ${selectedVendor?.name}`);
      console.log("🚀 DISPATCH:", vendorName);
    }

  };

  const cancelRequest = async (id) => {
    if (!window.confirm("Cancel this pickup request?")) return;

    try {
      await updateDoc(doc(db, "pickup_requests", id), {
        status: "cancelled",
        cancelledAt: Date.now()
      });
    } catch (error) {
      console.error(error);
      alert("Error cancelling request. Please try again.");
    }
  };

  const addVendor = async (e) => {
    e.preventDefault();

    try {
      await addDoc(collection(db, "vendors"), {
        name: vendorName,
        phone: vendorPhone,
        email: vendorEmail
      });

      alert("Vendor added!");

      setVendorName("");
      setVendorPhone("");
      setVendorEmail("");

    } catch (error) {
      console.error(error);
      alert("Error adding vendor");
    }
  };
  const deleteVendor = async (id) => {
    try {
      await deleteDoc(doc(db, "vendors", id));
    } catch (error) {
      console.error(error);
      alert("Error deleting vendor");
    }
  };
  const startEditVendor = (vendor) => {
    setEditingVendorId(vendor.id);
    setEditVendorName(vendor.name);
    setEditVendorPhone(vendor.phone);
    setEditVendorEmail(vendor.email || "");
  };

  const saveVendorEdit = async (id) => {
    try {
      await updateDoc(doc(db, "vendors", id), {
        name: editVendorName,
        phone: editVendorPhone,
        email: editVendorEmail
      });

      setEditingVendorId(null);
      setEditVendorName("");
      setEditVendorPhone("");
      setEditVendorEmail("");

    } catch (error) {
      console.error(error);
      alert("Error updating vendor");
    }
  };

  const cancelVendorEdit = () => {
    setEditingVendorId(null);
    setEditVendorName("");
    setEditVendorPhone("");
    setEditVendorEmail("");
  };

  const handleAdminLogin = async () => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        adminEmail,
        adminPassword
      );

      if (userCredential.user) {
        setIsAdmin(true);
      }

    } catch (error) {
      console.error(error);
      alert("Invalid admin login");
    }
  };

  const getFriendlyAuthError = (error) => {
    switch (error.code) {
      case "auth/invalid-credential":
      case "auth/wrong-password":
        return "Incorrect email or password. Please try again.";
      case "auth/user-not-found":
        return "No account found with that email. Try creating an account instead.";
      case "auth/invalid-email":
        return "Please enter a valid email address.";
      case "auth/too-many-requests":
        return "Too many attempts. Please wait a moment and try again.";
      case "auth/network-request-failed":
        return "Network error. Please check your connection and try again.";
      case "auth/weak-password":
        return "Password should be at least 6 characters.";
      case "auth/email-already-in-use":
        return "An account with this email already exists. Try signing in instead.";
      default:
        return "Unable to sign in. Please try again or contact support@onewast.com.";
    }
  };

  const handleLogout = async () => {
    await signOut(auth);

    setIsAdmin(false);
    setLoggedInVendor(null);
    setView("customer");
  };

  const handleDeleteAccount = async () => {
    setDeleteError("");

    if (!deletePassword) {
      setDeleteError("Please enter your password to confirm.");
      return;
    }

    setDeletingAccount(true);

    try {
      const currentUser = auth.currentUser;

      if (!currentUser || !currentUser.email) {
        throw new Error("No signed-in user found.");
      }

      // Firebase requires a recent login before allowing account deletion
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        deletePassword
      );

      await reauthenticateWithCredential(currentUser, credential);

      // Anonymize this customer's pickup request history in Firestore
      // (kept for tax/accounting records per our retention policy, but
      // stripped of personally identifying info)
      const requestsQuery = query(
        collection(db, "pickup_requests"),
        where("customerUid", "==", currentUser.uid)
      );

      const snapshot = await getDocs(requestsQuery);

      await Promise.all(
        snapshot.docs.map((document) =>
          updateDoc(doc(db, "pickup_requests", document.id), {
            customerEmail: "[deleted]",
            address: "[deleted]",
            customerUid: "[deleted]",
            deletedAt: Date.now(),
            accountDeleted: true
          })
        )
      );

      // Delete the Firebase Auth account itself
      await deleteUser(currentUser);

      // Also sign out of the native session if on a native platform
      if (Capacitor.isNativePlatform()) {
        try {
          await FirebaseAuthentication.signOut();
        } catch (nativeError) {
          console.error("Native sign-out error:", nativeError);
        }
      }

      setShowDeleteConfirm(false);
      setDeletePassword("");
      setCustomerEmail("");
      setUser(null);

      alert("Your account and data have been deleted.");

    } catch (error) {
      console.error("Delete account error:", error);

      if (error.code === "auth/wrong-password" || error.code === "auth/invalid-credential") {
        setDeleteError("Incorrect password. Please try again.");
      } else {
        setDeleteError(
          error.message || "Unable to delete account. Please try again or contact support@onewast.com."
        );
      }
    } finally {
      setDeletingAccount(false);
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "pending":
        return { color: "red", fontWeight: "bold" };
      case "assigned":
        return { color: "orange", fontWeight: "bold" };
      case "accepted": 
        return { color: "#2563eb", fontWeight: "bold" };
      case "completed":
        return { color: "green", fontWeight: "bold" };
      default:
        return {};
    }
  };
  const primaryButton = {
    background: "linear-gradient(135deg, #3b82f6, #2563eb)",
    color: "white",
    border: "none",
    borderRadius: "10px",
    padding: "14px",
    fontSize: "15px",
    cursor: "pointer",
    fontWeight: "500"
  };
    const cardStyle = {
      maxWidth: "500px",
      margin: "20px auto 60px",
      background: "white",
      padding: "24px",
      borderRadius: "16px",
      boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
      border: "1px solid #f1f5f9",
      transition: "all 0.2s ease",
      color: "#111827",
      colorScheme: "light"
    };
  const inputStyle = {
    width: "100%",
    height: "56px",
    padding: "0 16px",
    fontSize: "16px",
    borderRadius: "14px",
    border: "1px solid #d1d5db",
    boxSizing: "border-box",
    color: "#111827",
    backgroundColor: "#ffffff",
    colorScheme: "light"
  };
  const labelStyle = {
    fontWeight: "600",
    fontSize: "14px",
    color: "#374151"
  };
  const sectionTitle = {
    marginBottom: "20px",
    fontSize: "20px",
    fontWeight: "600"
  };
  const getCreatedAtMs = (req) =>
    req.createdAt?.seconds
      ? req.createdAt.seconds * 1000
      : req.createdAt || 0;

  const customerRequests = requests
    .filter((req) => req.customerEmail === customerEmail)
    .sort((a, b) => getCreatedAtMs(a) - getCreatedAtMs(b));

  const getStepStyle = (currentStatus, step) => {
    const steps = ["pending", "assigned", "completed"];
    const currentIndex = steps.indexOf(currentStatus);
    const stepIndex = steps.indexOf(step);

    return {
      backgroundColor: stepIndex <= currentIndex ? "#2563eb" : "#e5e7eb",
      color: stepIndex <= currentIndex ? "white" : "#6b7280",
      padding: "8px 12px",
      borderRadius: "999px",
      fontSize: "13px",
      fontWeight: "600"
    };
  };
  
  if (quoteSubmitted) {
    return (
      <QuoteSuccessPage
        onNewRequest={() => setQuoteSubmitted(false)}
      />
    );
  }

  if (isPaymentSuccess) {
    return <SuccessPage />;
  }

  if (!Capacitor.isNativePlatform() && !isLoaded) {
    return <div>Loading...</div>;
  }

  return (
      <div style={{
      padding: "20px",
      background: "#f3f4f6",
      minHeight: "100vh"
    }}>

  {view === "customer" && (
    <>
      {/* NAVBAR */}
      <div style={{
        position: "sticky",
        top: 0,
        zIndex: 1000,
        backdropFilter: "blur(10px)",
        background: "rgba(255,255,255,0.85)",
        borderBottom: "1px solid #e5e7eb",
        padding: "12px 20px",
        display: "flex",
        alignItems: "center",
        width: "100%",
        boxSizing: "border-box"
      }}>

        {/* LEFT SIDE */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <img src={logo} style={{ width: "36px" }} />
          <span style={{ fontWeight: "600", fontSize: "18px" }}>OneWast</span>
        </div>

        {/* HAMBURGER RIGHT SIDE */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          style={{
            marginLeft: "auto",
            background: "transparent",
            border: "none",
            fontSize: "32px",
            cursor: "pointer",
            color: "#111827",
            padding: "8px 12px",
            lineHeight: "1"
          }}
        >
          ☰
        </button>

        {menuOpen && (
          <div
            style={{
              position: "absolute",
              top: "65px",
              right: "20px",
              background: "white",
              borderRadius: "12px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
              padding: "15px",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              zIndex: 9999
            }}
          >
            <button
              style={primaryButton}
              onClick={() => {
                setView("customer");
                setMenuOpen(false);
              }}
            >
              Request Pickup
            </button>

            <button
              style={{ ...primaryButton, backgroundColor: "#374151" }}
              onClick={() => {
                window.open("https://onewast.com/privacy.html", "_blank");
                setMenuOpen(false);
              }}
            >
              Privacy Policy
            </button>

            <button
              style={{ ...primaryButton, backgroundColor: "#4b5563" }}
              onClick={() => {
                window.open("https://onewast.com/terms.html", "_blank");
                setMenuOpen(false);
              }}
            >
              Terms
            </button>

            <button
              style={{ ...primaryButton, backgroundColor: "#6b7280" }}
              onClick={() => {
                setView("contact");
                setMenuOpen(false);
              }}
            >
              Contact
            </button>

            <button
              style={{ ...primaryButton, backgroundColor: "#111827" }}
              onClick={() => {
                setView("admin");
                setMenuOpen(false);
              }}
            >
              {isAdmin ? "Admin Dashboard" : "Admin"}
            </button>

            <button
              style={{ ...primaryButton, backgroundColor: "#059669" }}
              onClick={() => {
                setView("vendor");
                setMenuOpen(false);
              }}
            >
              {loggedInVendor ? "Vendor Dashboard" : "Vendor"}
            </button>
          </div>
        )}

      </div>
      
      {/* HERO */}
    <div style={{
      padding: "20px 20px 28px",
      textAlign: "center",
      background: "linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)",
      borderRadius: "0 0 24px 24px"
    }}>
        <h1 style={{
          fontSize: "28px",
          marginBottom: "4px",
          color: "#0f172a"
        }}>
          Smart Waste Pickup, Simplified
        </h1>

        <p style={{
          fontSize: "15px",
          color: "#64748b"
        }}>
          Fast, reliable waste pickup at your fingertips.
        </p>
      </div>

      {!user && (
        <div style={cardStyle}>
          <h2>Customer Account</h2>

          <p style={{ color: "#6b7280" }}>
            Sign in to request a pickup and securely track your requests.
          </p>

          <form
            onSubmit={async (e) => {
              e.preventDefault();

              console.log("CUSTOMER LOGIN BUTTON CLICKED");

              setAuthError("");

              try {
                console.log(
                  "ATTEMPTING FIREBASE LOGIN:",
                  loginEmail
                );

                if (Capacitor.isNativePlatform()) {
                  console.log("USING NATIVE FIREBASE AUTH");

                  const nativeResult = await FirebaseAuthentication.signInWithEmailAndPassword({
                    email: loginEmail,
                    password: loginPassword
                  });

                  console.log("NATIVE FIREBASE LOGIN SUCCESS:", nativeResult.user);

                  // Unblock the UI immediately off the native result
                  setUser(nativeResult.user);
                  setCustomerEmail(nativeResult.user.email || "");

                  // Best-effort web sync — needed for Firestore rules to authorize reads/writes
                  try {
                    const credential = EmailAuthProvider.credential(loginEmail, loginPassword);
                    const webResult = await signInWithCredential(auth, credential);

                    setUser(webResult.user);
                    setCustomerEmail(webResult.user.email || "");
                    console.log("WEB FIREBASE SESSION CREATED:", webResult.user);
                  } catch (syncError) {
                    console.error("WEB SYNC FAILED (native session still active):", syncError);
                    setAuthError(getFriendlyAuthError(syncError));
                  }

                } else {
                  console.log("USING WEB FIREBASE AUTH");

                  const userCredential =
                    await signInWithEmailAndPassword(
                      auth,
                      loginEmail,
                      loginPassword
                    );

                  setUser(userCredential.user);

                  setCustomerEmail(
                    userCredential.user.email || ""
                  );

                  console.log(
                    "WEB FIREBASE LOGIN SUCCESS:",
                    userCredential.user
                  );
                }

              } catch (error) {
                console.error(
                  "CUSTOMER LOGIN ERROR:",
                  error
                );

                console.error(
                  "CODE:",
                  error.code
                );

                console.error(
                  "MESSAGE:",
                  error.message
                );

                setAuthError(getFriendlyAuthError(error));
              }
            }}
          >
            <input
              style={inputStyle}
              type="email"
              placeholder="Email"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              required
            />

            <br /><br />

            <input
              style={inputStyle}
              type="password"
              placeholder="Password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              required
            />

            <br /><br />

            <button
              type="submit"
              style={{
                ...primaryButton,
                width: "100%"
              }}
            >
              Sign In
            </button>

            <br /><br />

            <button
              type="button"
              style={{
                ...primaryButton,
                width: "100%",
                background: "#16a34a"
              }}
              onClick={async () => {
                setAuthError("");

                try {
                  await createUserWithEmailAndPassword(
                    auth,
                    loginEmail,
                    loginPassword
                  );
                } catch (error) {
                  console.error(error);
                  setAuthError(getFriendlyAuthError(error));
                }
              }}
            >
              Create Account
            </button>

            {authError && (
              <p
                style={{
                  color: "#dc2626",
                  marginTop: "15px"
                }}
              >
                {authError}
              </p>
            )}
          </form>
        </div>
      )}

      {user && !isAdmin && !loggedInVendor && (
        <div
          style={{
            maxWidth: "500px",
            margin: "20px auto",
            textAlign: "center"
          }}
        >
          <p style={{ color: "#111827" }}>
            Signed in as <strong>{user.email}</strong>
          </p>

          <button
            type="button"
            onClick={async () => {
              await signOut(auth);
              setCustomerEmail("");
            }}
          >
            Customer Logout
          </button>

          <br /><br />

          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            style={{
              background: "transparent",
              border: "1px solid #dc2626",
              color: "#dc2626",
              padding: "8px 16px",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "13px"
            }}
          >
            Delete My Account
          </button>
        </div>
      )}

      {showDeleteConfirm && (
        <div
          onClick={() => {
            if (!deletingAccount) {
              setShowDeleteConfirm(false);
              setDeletePassword("");
              setDeleteError("");
            }
          }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            padding: "20px"
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "white",
              borderRadius: "16px",
              padding: "28px",
              maxWidth: "420px",
              width: "100%"
            }}
          >
            <h2 style={{ color: "#dc2626", marginBottom: "10px" }}>
              Delete Your Account?
            </h2>

            <p style={{ color: "#6b7280", marginBottom: "20px", fontSize: "14px" }}>
              This will permanently delete your account and all of your pickup request
              history. This cannot be undone. Please enter your password to confirm.
            </p>

            <input
              type="password"
              placeholder="Enter your password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              style={{
                width: "100%",
                height: "48px",
                padding: "0 14px",
                fontSize: "15px",
                borderRadius: "10px",
                border: "1px solid #d1d5db",
                boxSizing: "border-box",
                marginBottom: "15px",
                color: "#111827",
                backgroundColor: "#ffffff",
                colorScheme: "light"
              }}
            />

            {deleteError && (
              <p style={{ color: "#dc2626", fontSize: "13px", marginBottom: "15px" }}>
                {deleteError}
              </p>
            )}

            <div style={{ display: "flex", gap: "10px" }}>
              <button
                type="button"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletePassword("");
                  setDeleteError("");
                }}
                disabled={deletingAccount}
                style={{
                  flex: 1,
                  padding: "12px",
                  borderRadius: "10px",
                  border: "1px solid #d1d5db",
                  background: "white",
                  color: "#111827",
                  colorScheme: "light",
                  cursor: "pointer"
                }}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleDeleteAccount}
                disabled={deletingAccount}
                style={{
                  flex: 1,
                  padding: "12px",
                  borderRadius: "10px",
                  border: "none",
                  background: "#dc2626",
                  color: "white",
                  cursor: deletingAccount ? "not-allowed" : "pointer",
                  opacity: deletingAccount ? 0.7 : 1
                }}
              >
                {deletingAccount ? "Deleting..." : "Delete Account"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{
        textAlign: "center",
        marginTop: "30px",
        marginBottom: "35px"
      }}>
        <h2 style={{ fontSize: "22px", marginBottom: "5px" }}>
          Request a Pickup
        </h2>
        <p style={{ color: "#6b7280", fontSize: "14px" }}>
          Takes seconds
        </p>
      </div>
      
      {/* FORM */}
        <div
          style={cardStyle}
          onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-3px)"}
          onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
        >
          <form onSubmit={handleSubmit}>
        <div>
          <label style={labelStyle}>Pickup Type</label>

          <select
            value={pickupType}
            onChange={(e) => {
              setPickupType(e.target.value);
              setItemType("");
            }}
            style={{
              ...inputStyle,
              appearance: "none",
              backgroundColor: "white"
            }}
          >
            <option value="">Select</option>
            <option value="fixed">Fixed Price Pickup</option>
            <option value="custom">Custom Quote</option>
          </select>
        </div>

        <br />

        {pickupType && (
          <div>
            <label style={labelStyle}>
              {pickupType === "fixed"
                ? "Item Type"
                : "Quote Type"}
            </label>

            <select
              value={itemType}
              onChange={(e) => setItemType(e.target.value)}
              style={{
                ...inputStyle,
                appearance: "none",
                backgroundColor: "white"
              }}
            >
              <option value="">Select</option>

              {pickupType === "fixed" ? (
                <>
                  <option value="mattress">Single Mattress ($125)</option>
                  <option value="chair">Chair ($120)</option>
                </>
              ) : (
                <>
                  <option value="couch">Couch / Sofa</option>
                  <option value="appliance">Appliance</option>
                  <option value="garage">Garage Cleanout</option>
                  <option value="basement">Basement Cleanout</option>
                  <option value="construction">Construction Debris</option>
                  <option value="yard">Yard Waste</option>
                  <option value="trash_bags">Trash Bags</option>
                  <option value="multiple_furniture">
                    Multiple Furniture Items
                  </option>
                  <option value="other">Other</option>
                </>
              )}
            </select>
          </div>
        )}

        <br />

        <div>
          <label style={labelStyle}>Address</label>

          {Capacitor.isNativePlatform() ? (
            <div style={{ position: "relative" }}>
              <input
                style={inputStyle}
                type="text"
                value={address}
                placeholder="Start typing address..."
                onChange={async (e) => {
                  const value = e.target.value;

                  setAddress(value);

                  if (value.trim().length < 3) {
                    setAddressPredictions([]);
                    return;
                  }

                  try {
                    setAddressSearching(true);

                    const result =
                      await PlacesPlugin.autocomplete({
                        query: value
                      });

                    setAddressPredictions(
                      result.predictions || []
                    );

                  } catch (error) {
                    console.error(
                      "Native address autocomplete error:",
                      error
                    );

                    setAddressPredictions([]);

                  } finally {
                    setAddressSearching(false);
                  }
                }}
              />

              {addressSearching && (
                <div
                  style={{
                    padding: "10px",
                    fontSize: "13px",
                    color: "#6b7280"
                  }}
                >
                  Searching...
                </div>
              )}

              {addressPredictions.length > 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: "60px",
                    left: 0,
                    right: 0,
                    background: "white",
                    color: "#111827",
                    colorScheme: "light",
                    border: "1px solid #d1d5db",
                    borderRadius: "10px",
                    zIndex: 9999,
                    overflow: "hidden",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.12)"
                  }}
                >
                  {addressPredictions.map(
                    (prediction, index) => (
                      <button
                        key={`${prediction.placeId}-${index}`}
                        type="button"
                        onClick={async () => {
                          try {
                            const details = await PlacesPlugin.placeDetails({
                              placeId: prediction.placeId
                            });

                            if (isWithinServiceArea(details.lat, details.lng)) {
                              setAddress(prediction.text);
                              setAddressError("");
                            } else {
                              setAddress("");
                              setAddressError(
                                "Sorry, this address is outside our service area (Houston and surrounding areas within about an hour of downtown)."
                              );
                            }
                          } catch (error) {
                            console.error("Place details error:", error);
                            setAddressError(
                              "Unable to verify this address. Please try again."
                            );
                          }

                          setAddressPredictions([]);
                        }}
                        style={{
                          width: "100%",
                          textAlign: "left",
                          padding: "12px 14px",
                          border: "none",
                          borderBottom:
                            index < addressPredictions.length - 1
                              ? "1px solid #e5e7eb"
                              : "none",
                          background: "white",
                          color: "#111827",
                          colorScheme: "light",
                          cursor: "pointer"
                        }}
                      >
                        {prediction.text}
                      </button>
                    )
                  )}
                </div>
              )}
            </div>
          ) : (
            <Autocomplete
              onLoad={(autocomplete) => {
                autocompleteRef.current = autocomplete;
              }}
              onPlaceChanged={() => {
                if (autocompleteRef.current) {
                  const place = autocompleteRef.current.getPlace();

                  if (place.formatted_address && place.geometry) {
                    const lat = place.geometry.location.lat();
                    const lng = place.geometry.location.lng();

                    if (isWithinServiceArea(lat, lng)) {
                      setAddress(place.formatted_address);
                      setAddressError("");
                    } else {
                      setAddress("");
                      setAddressError(
                        "Sorry, this address is outside our service area (Houston and surrounding areas within about an hour of downtown)."
                      );
                    }
                  }
                }
              }}
            >
              <input
                style={inputStyle}
                type="text"
                value={address}
                onChange={(e) =>
                  setAddress(e.target.value)
                }
                placeholder="Start typing address..."
              />
            </Autocomplete>
          )}

          {addressError && (
            <p style={{ color: "#dc2626", fontSize: "13px", marginTop: "6px" }}>
              {addressError}
            </p>
          )}
        </div>

        <br />

        <div>
          <label style={labelStyle}>Email</label>
          <input
            style={inputStyle}
            type="email"
            value={customerEmail}
            onChange={(e) => setCustomerEmail(e.target.value)}
          />
        </div>

        <br />

        <div>
          <label style={labelStyle}>Photos (Required for Accurate Pickup)</label>

          <input
            type="file"
            multiple
            accept="image/*"
            onChange={(e) => setPhotos(Array.from(e.target.files))}
            style={{
              width: "100%",
              padding: "12px",
              borderRadius: "14px",
              border: "1px solid #d1d5db",
              boxSizing: "border-box"
            }}
          />

          <small>
            Upload up to 5 photos so we can accurately price your pickup.
          </small>
        </div>

        <br />

        <ReCAPTCHA
          sitekey="6Le5o-8sAAAAAF7y-pyHZsIbkK0ajkp38DuozNmw"
          onChange={(value) => setCaptchaValue(value)}
        />

        <br />

        <button
          type="submit"
          disabled={loading}
          style={{
            ...primaryButton,
            width: "100%",
            marginTop: "20px",
            padding: "14px",
            fontSize: "16px",
            opacity: loading ? 0.7 : 1,
            cursor: loading ? "not-allowed" : "pointer"
          }}
        >
          {loading ? "Submitting..." : "Submit Request"}
        </button>

        {(pickupType || itemType || address || photos.length > 0) && !loading && (
          <button
            type="button"
            onClick={() => {
              setPickupType("");
              setItemType("");
              setAddress("");
              setPhotos([]);
              setCaptchaValue(null);
              setAddressError("");
              setAddressPredictions([]);
            }}
            style={{
              width: "100%",
              marginTop: "10px",
              padding: "12px",
              fontSize: "14px",
              background: "transparent",
              border: "1px solid #d1d5db",
              borderRadius: "10px",
              color: "#6b7280",
              cursor: "pointer"
            }}
          >
            Start Over
          </button>
        )}
      </form>
      </div>

    {customerEmail && customerRequests.length > 0 && (
      <div style={{
        maxWidth: "600px",
        margin: "20px auto",
        background: "white",
        padding: "20px",
        borderRadius: "12px",
        boxShadow: "0 10px 25px rgba(0,0,0,0.05)",
        color: "#111827",
        colorScheme: "light"
      }}>
        <h3 style={{ marginBottom: "15px" }}>Your Latest Request</h3>

        {[customerRequests[customerRequests.length - 1]].map((req) => (
          <div
            id={`request-${req.id}`}  // 👈 REQUIRED
            key={req.id}
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: "10px",
            padding: "12px",
            marginBottom: "10px"
          }}>
            
            <p><strong>Type:</strong> {req.itemType}</p>
            <p><strong>Address:</strong> {req.address}</p>

            <p>
              <strong>Status:</strong>{" "}
              <span style={getStatusStyle(req.status)}>
                {req.status.toUpperCase()}
              </span>
            </p>

            {(req.status === "pending" || req.status === "quote_requested") && (
              <button
                onClick={() => cancelRequest(req.id)}
                style={{
                  marginTop: "10px",
                  padding: "8px 16px",
                  borderRadius: "8px",
                  border: "1px solid #dc2626",
                  background: "white",
                  color: "#dc2626",
                  cursor: "pointer",
                  fontWeight: "600"
                }}
              >
                Cancel Request
              </button>
            )}
          
          </div>
        ))}
      </div>
    )} 
    
       <button
          onClick={() => setShowHistory(!showHistory)}
          style={{
            marginTop: "15px",
            padding: "10px 16px",
            borderRadius: "8px",
            border: "none",
            background: "#111827",
            color: "white",
            cursor: "pointer"
          }}
        >
          {showHistory ? "Hide History" : "View History"}
        </button> 

        {showHistory && (
          <div style={{ marginTop: "20px", color: "#111827" }}>
            <h4>Previous Requests</h4>

            {customerRequests
              .slice(0, customerRequests.length - 1)
              .reverse()
              .map((req) => (
                <div
                  key={req.id}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "10px",
                    padding: "12px",
                    marginBottom: "10px",
                    background: "#f9fafb",
                    color: "#111827",
                    colorScheme: "light"
                  }}
                >
                  <p><strong>Type:</strong> {req.itemType}</p>

                  <p><strong>Address:</strong> {req.address}</p>

                  <p>
                    <strong>Status:</strong>{" "}
                    <span style={getStatusStyle(req.status)}>
                      {req.status.toUpperCase()}
                    </span>
                  </p>

                  <p>
                    <strong>Amount Paid:</strong>{" "}
                    {req.amountPaid
                      ? `$${(req.amountPaid / 100).toFixed(2)}`
                      : "—"}
                  </p>

                  {(req.status === "pending" || req.status === "quote_requested") && (
                    <button
                      onClick={() => cancelRequest(req.id)}
                      style={{
                        marginTop: "10px",
                        padding: "8px 16px",
                        borderRadius: "8px",
                        border: "1px solid #dc2626",
                        background: "white",
                        color: "#dc2626",
                        cursor: "pointer",
                        fontWeight: "600"
                      }}
                    >
                      Cancel Request
                    </button>
                  )}
                </div>
              ))}
          </div>
        )}

      <div style={{
          textAlign: "center",
          padding: "40px 20px",
          color: "#6b7280"
        }}>
          <p style={{ marginBottom: "10px" }}>
            Trusted by local businesses & homeowners
          </p>

          <div style={{
            display: "flex",
            justifyContent: "center",
            gap: "20px",
            fontSize: "14px"
          }}>
            <span>✔ Fast Response</span>
            <span>✔ Professional Pickup</span>
            <span>✔ Secure Requests</span>
          </div>
      </div>

      </>
  )}

  {view === "admin" && (
    <>
      {!isAdmin ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleAdminLogin();
          }}
          style={{ color: "#111827", colorScheme: "light" }}
        >
          <h2>Admin Login</h2>

          <input
            type="email"
            placeholder="Admin email"
            value={adminEmail}
            onChange={(e) => setAdminEmail(e.target.value)}
            style={{ color: "#111827", backgroundColor: "#ffffff", colorScheme: "light" }}
          />

          <input
            type="password"
            placeholder="Enter password"
            value={adminPassword}
            onChange={(e) => setAdminPassword(e.target.value)}
            style={{ color: "#111827", backgroundColor: "#ffffff", colorScheme: "light" }}
          />

          <button
            type="submit"
            style={{ color: "#111827", backgroundColor: "#ffffff", colorScheme: "light" }}
          >
            Login
          </button>
        </form>
      ) : (
        <div style={{ color: "#111827", colorScheme: "light" }}>
        <hr />

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h1>Admin Dashboard</h1>

          <button
            onClick={() => setView("customer")}
            style={{
              marginBottom: "15px",
              padding: "10px 16px",
              borderRadius: "8px",
              border: "none",
              background: "#2563eb",
              color: "white",
              cursor: "pointer"
            }}
          >
            ← Back to Home
          </button>

          <button 
            onClick={handleLogout}
            style={{
              backgroundColor: "red",
              color: "white",
              padding: "8px 12px",
              border: "none",
              cursor: "pointer"
            }}
          >
            Logout
          </button>
        </div>

      {/* Add Vendor */}
      <h2>Add Vendor</h2>

      <form onSubmit={addVendor}>
        <div>
          <label>Name:</label>
          <input
            type="text"
            value={vendorName}
            onChange={(e) => setVendorName(e.target.value)}
            style={{ color: "#111827", backgroundColor: "#ffffff", colorScheme: "light" }}
          />
        </div>

        <br />

        <div>
          <label>Phone:</label>
          <input
            type="text"
            value={vendorPhone}
            onChange={(e) => setVendorPhone(e.target.value)}
            style={{ color: "#111827", backgroundColor: "#ffffff", colorScheme: "light" }}
          />
        </div>

        <br />

        <div>
          <label>Email:</label>
          <input
            type="email"
            value={vendorEmail}
            onChange={(e) => setVendorEmail(e.target.value)}
            style={{ color: "#111827", backgroundColor: "#ffffff", colorScheme: "light" }}
          />
        </div>

        <br />

        <button type="submit">Add Vendor</button>
      </form>
      
      <hr />

      <h2>Vendor List</h2>

      <table border="1" cellPadding="10">
        <thead>
          <tr>
            <th>Name</th>
            <th>Phone</th>
            <th>Email</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {vendors.map((vendor) => (
            <tr key={vendor.id}>
              <td>
                {editingVendorId === vendor.id ? (
                  <input
                    type="text"
                    value={editVendorName}
                    onChange={(e) => setEditVendorName(e.target.value)}
                  />
                 ) : (
                  vendor.name
                )}
              </td>

              <td>
                {editingVendorId === vendor.id ? (
                  <input
                    type="text"
                    value={editVendorPhone}
                    onChange={(e) => setEditVendorPhone(e.target.value)}
                  />
                ) : (
                  vendor.phone
                )}
              </td>

              <td>
                {editingVendorId === vendor.id ? (
                  <input
                    type="email"
                    value={editVendorEmail}
                    onChange={(e) => setEditVendorEmail(e.target.value)}
                  />
                ) : (
                  vendor.email || "—"
                )}
              </td>

              <td>
                {editingVendorId === vendor.id ? (
                  <>
                    <button onClick={() => saveVendorEdit(vendor.id)}>Save</button>
                    <button onClick={cancelVendorEdit}>Cancel</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => startEditVendor(vendor)}>Edit</button>
                    <button onClick={() => deleteVendor(vendor.id)}>Delete</button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <h3>Filter Requests</h3>

      <select
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
      >
        <option value="all">All</option>
        <option value="pending">Pending</option>
        <option value="quote_requested">Quote Requested</option>
        <option value="quoted">Quoted</option>
        <option value="assigned">Assigned</option>
        <option value="accepted">Accepted</option>
        <option value="on_the_way">On The Way</option>
        <option value="completed">Completed</option>
      </select>

      <br /><br />

      <h2>Pickup Requests</h2>

      <table border="1" cellPadding="10">
        <thead>
          <tr>
            <th>Item Type</th>
            <th>Address</th>
            <th>Status</th>
            <th>Vendor</th>
            <th>Created</th>
            <th>Assigned</th>
            <th>Completed</th>
            <th>Duration</th>
            <th>Photos</th>
            <th>Quote</th>
            <th>Vendor Payout</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {requests
            .filter((req) =>
              statusFilter === "all" ? true : req.status === statusFilter
            )
            .sort((a, b) => {
              const aTime = a.createdAt?.seconds
                ? a.createdAt.seconds * 1000
                : a.createdAt || 0;

              const bTime = b.createdAt?.seconds
                ? b.createdAt.seconds * 1000
                : b.createdAt || 0;

              return bTime - aTime;
            })
            .map((req) => (
            <tr key={req.id}>
              <td>{req.itemType}</td>
              <td>{req.address}</td>
              <td style={getStatusStyle(req.status)}>
                {req.status
                  .replaceAll("_", " ")
                  .replace(/\b\w/g, c => c.toUpperCase())}
              </td>
              <td>{req.vendor || "—"}</td>
              
              <td>
                {req.createdAt
                  ? new Date(
                      req.createdAt.seconds
                        ? req.createdAt.seconds * 1000
                        : req.createdAt
                    ).toLocaleString([], {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit"
                    })
                  : "—"}
              </td>

              <td>
                {req.assignedAt
                  ? new Date(
                      req.assignedAt.seconds
                        ? req.assignedAt.seconds * 1000
                        : req.assignedAt
                    ).toLocaleString([], {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit"
                    })
                  : "—"}
              </td>

              <td>
                {req.completedAt
                    ? new Date(
                        req.completedAt.seconds
                          ? req.completedAt.seconds * 1000
                          : req.completedAt
                      ).toLocaleString([], {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit"
                      })
                    : "—"}
                </td>
              
                <td>
                  {req.assignedAt && req.completedAt
                    ? (() => {
                        const assigned = new Date(
                          req.assignedAt.seconds
                            ? req.assignedAt.seconds * 1000
                            : req.assignedAt
                        );

                        const completed = new Date(
                          req.completedAt.seconds
                            ? req.completedAt.seconds * 1000
                            : req.completedAt
                        );

                        const diffMs = completed - assigned;
                        const diffMin = Math.floor(diffMs / 60000);

                        return `${diffMin} min`;
                      })()
                    : "—"}
                </td>

                <td>
                  {req.photoUrls?.length > 0 ? (
                    <button
                      onClick={() => {
                        setSelectedPhotos(req.photoUrls);
                        setCurrentPhotoIndex(0);
                        setPhotoViewerOpen(true);
                      }}
                    >
                      View Photos
                    </button>
                  ) : (
                    "—"
                  )}
                </td>

                <td>
                  {req.status === "quote_requested" ? (
                    <input
                      type="number"
                      placeholder="$ Amount"
                      value={req.quoteAmount || ""}
                      onChange={async (e) => {
                        await updateDoc(
                          doc(db, "pickup_requests", req.id),
                          {
                            quoteAmount: e.target.value
                          }
                        );
                      }}
                      style={{
                        width: "100px",
                        padding: "6px"
                      }}
                    />
                  ) : req.quoteAmount ? (
                    <>
                      ${req.quoteAmount}

                      {req.paymentStatus === "paid" && (
                        <>
                          <br />
                          <span
                            style={{
                              color: "#16a34a",
                              fontWeight: "bold",
                              fontSize: "12px"
                            }}
                          >
                            ✓ Paid
                          </span>
                        </>
                      )}
                    </>
                  ) : (
                    "—"
                  )}
                </td>
                
                <td>
                  <input
                    type="number"
                    placeholder="$ Vendor Payout"
                    value={req.vendorPayout ? (req.vendorPayout / 100) : ""}
                    onChange={async (e) => {
                      const dollars = e.target.value;
                      await updateDoc(
                        doc(db, "pickup_requests", req.id),
                        {
                          vendorPayout: dollars === "" ? null : Math.round(Number(dollars) * 100)
                        }
                      );
                    }}
                    style={{
                      width: "110px",
                      padding: "6px"
                    }}
                  />
                </td>

                <td>
                  {req.status === "quote_requested" && req.quoteAmount && (
                    <button
                      onClick={async () => {
                        try {

                          const response = await fetch(
                            "https://us-central1-onewast.cloudfunctions.net/sendCustomQuote",
                            {
                              method: "POST",
                              headers: {
                                "Content-Type": "application/json",
                              },
                              body: JSON.stringify({
                                requestId: req.id,
                                customerEmail: req.customerEmail,
                                amount: req.quoteAmount,
                                itemType: req.itemType,
                                address: req.address,
                              }),
                            }
                          );

                          const data = await response.json();

                          if (data.success) {

                            await updateDoc(
                              doc(db, "pickup_requests", req.id),
                              {
                                status: "quoted"
                              }
                            );

                            alert("Quote sent!");
                          } else {
                            alert("Failed to send quote.");
                          }

                        } catch (error) {
                          console.error(error);
                          alert("Error sending quote.");
                        }
                      }}
                    >
                      Send Quote
                    </button>
                  )}

                    {req.status === "pending" && (
                      <select
                        onChange={(e) =>
                          updateRequest(req.id, "assigned", e.target.value, req)
                        }
                        defaultValue=""
                      >
                        <option value="">Assign Vendor</option>
                        {vendors.map((vendor) => (
                          <option key={vendor.id} value={vendor.id}>
                            {vendor.name}
                          </option>
                        ))}
                      </select>
                    )}

                    {req.status === "assigned" && (
                      <span
                        style={{
                          color: "#f59e0b",
                          fontWeight: "bold"
                        }}
                      >
                        Waiting for Vendor
                      </span>
                    )}

                    {req.status === "accepted" && (
                      <span
                        style={{
                          color: "#2563eb",
                          fontWeight: "bold"
                        }}
                      >
                        Vendor Accepted
                      </span>
                    )}

                    {req.status === "on_the_way" && (
                      <span
                        style={{
                          color: "#16a34a",
                          fontWeight: "bold"
                        }}
                      >
                        🚚 On the Way
                      </span>
                    )}

                    {req.status === "completed" && (
                      <span style={{ color: "green", fontWeight: "bold" }}>
                        ✔ Completed
                      </span>
                    )}

                    {req.pickupType === "fixed" &&
                      req.paymentStatus === "paid" &&
                      req.status !== "completed" &&
                      req.status !== "cancelled" && (
                        <button
                          onClick={async () => {
                            const reason = window.prompt(
                              "Reason for rejecting this request? (shown to customer)",
                              "The item photo doesn't match what was selected."
                            );

                            if (reason === null) return;

                            if (!window.confirm(
                              "This will refund the customer's payment in full and cancel this request. Continue?"
                            )) return;

                            try {
                              await updateDoc(doc(db, "pickup_requests", req.id), {
                                status: "cancelled",
                                cancelReason: reason,
                                cancelledAt: Date.now(),
                              });
                              alert("Request cancelled — refund will be processed automatically.");
                            } catch (error) {
                              console.error(error);
                              alert("Error cancelling request.");
                            }
                          }}
                          style={{
                            background: "#dc2626",
                            color: "white",
                            border: "none",
                            padding: "8px 14px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            marginLeft: "8px",
                          }}
                        >
                          Reject & Refund
                        </button>
                      )}
                </td>
          </tr>
        ))}
      </tbody>
      </table>
        </div>
      )}
    </>
  )}

  {view === "vendor" && (
    <>
      {!loggedInVendor ? (
        <form
          onSubmit={async (e) => {
            e.preventDefault();

            try {
              const userCredential = await signInWithEmailAndPassword(
                auth,
                vendorEmailLogin,
                vendorPassword
              );

              const user = userCredential.user;

              const vendor = vendors.find(
                (v) =>
                  v.email?.toLowerCase() === user.email.toLowerCase()
              );

              if (vendor) {
                setLoggedInVendor(vendor);
              } else {
                alert("Vendor not found in database");
              }

            } catch (error) {
              console.error(error);
              alert("Login failed");
            }
          }}
        >
          <h2 style={{ color: "#111827" }}>Vendor Login</h2>

          <button
            type="button"
            onClick={() => setView("customer")}
            style={{
              marginBottom: "20px",
              padding: "10px 16px",
              borderRadius: "8px",
              border: "none",
              background: "#2563eb",
              color: "white",
              cursor: "pointer"
            }}
          >
            ← Back to Home
          </button>

          <input
            type="email"
            placeholder="Enter your email"
            value={vendorEmailLogin}
            onChange={(e) => setVendorEmailLogin(e.target.value)}
            style={{ color: "#111827", backgroundColor: "#ffffff", colorScheme: "light" }}
          />

          <input
            type="password"
            placeholder="Enter password"
            value={vendorPassword}
            onChange={(e) => setVendorPassword(e.target.value)}
            style={{ color: "#111827", backgroundColor: "#ffffff", colorScheme: "light" }}
          />

          <button
            type="submit"
            style={{ color: "#111827", backgroundColor: "#ffffff", colorScheme: "light" }}
          >
            Login
          </button>
        </form>
      ) : (
        <div style={{ color: "#111827", colorScheme: "light" }}>
          <h2>Welcome, {loggedInVendor.name}</h2>

          <button
            onClick={() => {
              setView("customer");
          }}
            style={{
              marginBottom: "15px",
              padding: "10px 16px",
              borderRadius: "8px",
              border: "none",
              background: "#2563eb",
              color: "white",
              cursor: "pointer"
            }}
          >
            ← Back to Home
          </button>

          <button
            onClick={async () => {
              await signOut(auth);

              setLoggedInVendor(null);
              setView("customer");
            }}
            style={{ marginBottom: "10px" }}
          >
            Logout
          </button>

          <h3>Your Assigned Jobs</h3>

          <div style={{
            background: "#f0fdf4",
            border: "1px solid #86efac",
            borderRadius: "10px",
            padding: "16px",
            marginBottom: "20px",
          }}>
            <p style={{ margin: 0, fontSize: "14px", color: "#166534" }}>
              Total Earned (Completed Jobs)
            </p>
            <p style={{ margin: "4px 0 0", fontSize: "28px", fontWeight: "bold", color: "#166534" }}>
              ${(
                requests
                  .filter((r) => r.vendorId === loggedInVendor.id && r.status === "completed")
                  .reduce((sum, r) => sum + (r.vendorPayout || 0), 0) / 100
              ).toFixed(2)}
            </p>
          </div>

          <table border="1" cellPadding="10">
            <thead>
              <tr>
                <th>Item</th>
                <th>Address</th>
                <th>Paid</th>
                <th>Photos</th>
                <th>Status</th>
                <th>Payout</th>
                <th>Assigned</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests
                .filter((req) => req.vendorId === loggedInVendor.id)
                .map((req) => (
                  <tr key={req.id}>
                    <td>{req.itemType}</td>

                    <td>{req.address}</td>

                    <td>
                      {req.paymentStatus === "paid" ? (
                        <span style={{ color: "green", fontWeight: "bold" }}>
                          ✓ Paid
                        </span>
                      ) : (
                        "Awaiting Payment"
                      )}
                    </td>

                    <td>
                      {req.photoUrls?.length > 0 ? (
                        <button
                          onClick={() => {
                            setSelectedPhotos(req.photoUrls);
                            setCurrentPhotoIndex(0);
                            setPhotoViewerOpen(true);
                          }}
                        >
                          View Photos
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>

                    <td>{req.status}</td>

                    <td>
                      {req.vendorPayout ? `$${(req.vendorPayout / 100).toFixed(2)}` : "—"}
                    </td>

                    <td>
                      {req.assignedAt
                        ? new Date(
                            req.assignedAt.seconds
                              ? req.assignedAt.seconds * 1000
                              : req.assignedAt
                          ).toLocaleString()
                        : "—"}
                    </td>

                    <td>
                      {req.status === "assigned" && (
                        <div
                          style={{
                            display: "flex",
                            gap: "10px"
                          }}
                        >
                          <button
                            onClick={() =>
                              updateRequest(
                                req.id,
                                "accepted",
                                loggedInVendor.id,
                                req
                              )
                            }
                            style={{
                              background: "#16a34a",
                              color: "white",
                              border: "none",
                              padding: "8px 14px",
                              borderRadius: "8px",
                              cursor: "pointer"
                            }}
                          >
                            Accept
                          </button>

                          <button
                            onClick={() => {
                              if (
                                window.confirm(
                                  "Decline this pickup?"
                                )
                              ) {
                                updateRequest(
                                  req.id,
                                  "pending",
                                  "",
                                  req
                                );
                              }
                            }}
                            style={{
                              background: "#dc2626",
                              color: "white",
                              border: "none",
                              padding: "8px 14px",
                              borderRadius: "8px",
                              cursor: "pointer"
                            }}
                          >
                            Decline
                          </button>
                        </div>
                      )}

                      {req.status === "accepted" && (
                        <button
                          onClick={() =>
                            updateRequest(
                              req.id,
                              "on_the_way",
                              loggedInVendor.id,
                              req
                            )
                          }
                        >
                          Start Trip
                        </button>
                      )}

                      {req.status === "on_the_way" && (
                        <button
                          onClick={() =>
                            updateRequest(
                              req.id,
                              "completed",
                              loggedInVendor.id,
                              req
                            )
                          }
                        >
                          Mark Complete
                        </button>
                      )}

                      {req.status === "completed" && (
                        <span style={{ color: "green" }}>
                          ✔ Done
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )}

  {view === "contact" && (
    <div style={{ maxWidth: "900px", margin: "0 auto", padding: "40px", color: "#111827" }}>
      <button
        onClick={() => setView("customer")}
        style={primaryButton}
      >
        ← Back Home
      </button>

      <h1>Contact Us</h1>

      <p>Email: support@onewast.com</p>

      <p>
        For support, billing questions, or pickup inquiries,
        please contact us via email.
      </p>
    </div>
  )}

  {photoViewerOpen && (
    <div
      onClick={() => setPhotoViewerOpen(false)}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.85)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white",
          borderRadius: "16px",
          padding: "25px",
          width: "90%",
          maxWidth: "850px",
          textAlign: "center"
        }}
      >
        <h2 style={{ marginBottom: "20px", color: "#111827" }}>
          Pickup Photos
        </h2>

        <img
          src={selectedPhotos[currentPhotoIndex]}
          alt="Pickup"
          style={{
            width: "100%",
            maxHeight: "500px",
            objectFit: "contain",
            borderRadius: "12px",
            border: "1px solid #e5e7eb"
          }}
        />

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "10px",
            marginTop: "20px",
            flexWrap: "wrap"
          }}
        >
          {selectedPhotos.map((photo, index) => (
            <img
              key={index}
              src={photo}
              alt={`Thumbnail ${index + 1}`}
              onClick={() => setCurrentPhotoIndex(index)}
              style={{
                width: "80px",
                height: "80px",
                objectFit: "cover",
                borderRadius: "8px",
                cursor: "pointer",
                border:
                  currentPhotoIndex === index
                    ? "3px solid #2563eb"
                    : "2px solid #e5e7eb",
                opacity: currentPhotoIndex === index ? 1 : 0.75,
                transition: "all 0.2s ease"
              }}
            />
          ))}
        </div>

        <p
          style={{
            marginTop: "15px",
            fontWeight: "600",
            color: "#6b7280"
          }}
        >
          {currentPhotoIndex + 1} of {selectedPhotos.length}
        </p>

        <p
          style={{
            color: "#6b7280",
            fontSize: "13px",
            marginTop: "8px"
          }}
        >
          Tip: Use ← → arrow keys or click a thumbnail.
        </p>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "20px"
          }}
        >
          <button
            disabled={currentPhotoIndex === 0}
            onClick={() =>
              setCurrentPhotoIndex((prev) => prev - 1)
            }
            style={{
              padding: "10px 20px",
              borderRadius: "8px",
              border: "none",
              color: "#111827",
              backgroundColor: "transparent",
              colorScheme: "light",
              cursor:
                currentPhotoIndex === 0
                  ? "default"
                  : "pointer",
              opacity:
                currentPhotoIndex === 0 ? 0.4 : 1
            }}
          >
            ◀ Previous
          </button>

          <button
            disabled={
              currentPhotoIndex ===
              selectedPhotos.length - 1
            }
            onClick={() =>
              setCurrentPhotoIndex((prev) => prev + 1)
            }
            style={{
              padding: "10px 20px",
              borderRadius: "8px",
              border: "none",
              color: "#111827",
              backgroundColor: "transparent",
              colorScheme: "light",
              cursor:
                currentPhotoIndex ===
                selectedPhotos.length - 1
                  ? "default"
                  : "pointer",
              opacity:
                currentPhotoIndex ===
                selectedPhotos.length - 1
                  ? 0.4
                  : 1
            }}
          >
            Next ▶
          </button>
        </div>

        <button
          onClick={() => setPhotoViewerOpen(false)}
          style={{
            marginTop: "25px",
            padding: "12px 30px",
            background: "#2563eb",
            color: "white",
            border: "none",
            borderRadius: "10px",
            cursor: "pointer"
          }}
        >
          Close
        </button>
      </div>
    </div>
  )}
    </div>
);
}

export default App;